import React, { useState, useEffect } from 'react';
import {
  HelpCircle,
  Search,
  Book,
  MessageCircle,
  Mail,
  Phone,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Play,
  FileText,
  BarChart3,
  Bell,
  Shield,
  Upload,
  Users,
  Settings,
  CheckCircle,
  Loader2,
  Send,
  AlertCircle,
  Lock,
  Eye,
  Cookie,
  Scale,
  ShieldCheck,
  Key,
  Server,
  Fingerprint,
  X,
} from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const Help = () => {
  const { t, translations, language } = useTranslation();
  const { user } = useAuth();


  const [faqData, setFaqData] = useState({});
  const [legalInfo, setLegalInfo] = useState({});
  const [isLoading, setIsLoading] = useState(true);


  const modals = translations[language]?.help?.modals || {};
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    priority: 'normal',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [activeModal, setActiveModal] = useState(null);


  useEffect(() => {
    const loadHelpData = async () => {
      setIsLoading(true);
      try {

        const faqResponse = await window.electronAPI?.help?.getAllFaqs(language);
        if (faqResponse?.success && faqResponse.data) {
          setFaqData(faqResponse.data);
        } else {

          const faqContent = translations[language]?.help?.faqContent || {};
          setFaqData({
            'getting-started': faqContent.gettingStarted || [],
            loans: faqContent.loans || [],
            covenants: faqContent.covenants || [],
            alerts: faqContent.alerts || [],
            reports: faqContent.reports || [],
            import: faqContent.import || [],
            users: faqContent.users || [],
            settings: faqContent.settings || [],
          });
        }


        const legalResponse = await window.electronAPI?.help?.getAllLegalInfo(language);
        if (legalResponse?.success && legalResponse.data) {
          setLegalInfo(legalResponse.data);
        }
      } catch (error) {
        console.error('Error loading help data:', error);

        const faqContent = translations[language]?.help?.faqContent || {};
        setFaqData({
          'getting-started': faqContent.gettingStarted || [],
          loans: faqContent.loans || [],
          covenants: faqContent.covenants || [],
          alerts: faqContent.alerts || [],
          reports: faqContent.reports || [],
          import: faqContent.import || [],
          users: faqContent.users || [],
          settings: faqContent.settings || [],
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadHelpData();
  }, [language, translations]);

  const categories = [
    { id: 'getting-started', label: t('help.categories.gettingStarted'), icon: Play },
    { id: 'loans', label: t('help.categories.loans'), icon: FileText },
    { id: 'covenants', label: t('help.categories.covenants'), icon: BarChart3 },
    { id: 'alerts', label: t('help.categories.alerts'), icon: Bell },
    { id: 'reports', label: t('help.categories.reports'), icon: FileText },
    { id: 'import', label: t('help.categories.import'), icon: Upload },
    { id: 'users', label: t('help.categories.users'), icon: Users },
    { id: 'settings', label: t('help.categories.settings'), icon: Settings },
  ];

  const quickLinks = [
    { icon: Book, title: t('help.documentation'), desc: t('help.docDescription'), url: '#' },
    { icon: Play, title: t('help.tutorials'), desc: t('help.tutorialsDescription'), url: '#' },
    { icon: FileText, title: t('help.apiDocumentation'), desc: t('help.apiDescription'), url: '#' },
    { icon: Shield, title: t('help.securityPolicy'), desc: t('help.securityPolicyDescription'), url: '#' },
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      setSubmitMessage({ type: 'error', text: t('help.contactForm.error') });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await window.electronAPI?.help?.createContactMessage(
        {
          subject: contactForm.subject.trim(),
          message: contactForm.message.trim(),
          priority: contactForm.priority,
        },
        user?.id || null
      );

      if (response?.success) {
        setSubmitMessage({ type: 'success', text: t('help.contactForm.success') });
        setContactForm({ subject: '', message: '', priority: 'normal' });
      } else {
        setSubmitMessage({ type: 'error', text: response?.error || t('help.contactForm.errorGeneric') });
      }
    } catch (error) {
      console.error('Error sending contact message:', error);
      setSubmitMessage({ type: 'error', text: t('help.contactForm.errorGeneric') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredFaqs = searchQuery
    ? Object.entries(faqData).flatMap(([category, faqs]) =>
        faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        ).map(faq => ({ ...faq, category }))
      )
    : faqData[activeCategory] || [];

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            {t('help.title')}
          </h1>
          <p className="text-slate-500 max-w-xl mx-auto">
            {t('help.subtitle')}
          </p>
        </div>

        {}
        <div className="max-w-2xl mx-auto mb-10">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('help.searchPlaceholder')}
              className="w-full pl-12 pr-4 py-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg shadow-sm"
            />
          </div>
        </div>

        {}
        <div className="grid md:grid-cols-4 gap-4 mb-10">
          {quickLinks.map((link, index) => (
            <a
              key={index}
              href={link.url}
              className="p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <link.icon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                    {link.title}
                  </h3>
                  <p className="text-sm text-slate-500">{link.desc}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-800">{t('help.faq')}</h2>
              </div>

              {!searchQuery && (
                <div className="flex overflow-x-auto border-b border-slate-200">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                        activeCategory === cat.id
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <cat.icon className="w-4 h-4" />
                      {cat.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="divide-y divide-slate-200">
                {filteredFaqs.length > 0 ? (
                  filteredFaqs.map((faq, index) => (
                    <div key={index} className="p-0">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-medium text-slate-800 pr-4">{faq.question}</span>
                        {expandedFaq === index ? (
                          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      {expandedFaq === index && (
                        <div className="px-4 pb-4">
                          <p className="text-slate-600 bg-slate-50 rounded-lg p-4">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t('help.noResults')} "{searchQuery}"</p>
                    <button
                      onClick={() => setSearchQuery('')}
                      className="mt-2 text-blue-600 hover:underline"
                    >
                      {t('help.clearSearch')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {}
          <div className="space-y-6">
            {}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{t('help.contactUs')}</h3>
                  <p className="text-sm text-slate-500">{t('help.contactUsSubtitle')}</p>
                </div>
              </div>

              {submitMessage.text && (
                <div
                  className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
                    submitMessage.type === 'success'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {submitMessage.type === 'success' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  {submitMessage.text}
                </div>
              )}

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('help.contactForm.subject')}
                  </label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) =>
                      setContactForm((prev) => ({ ...prev, subject: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('help.contactForm.subjectPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('help.contactForm.priority')}
                  </label>
                  <select
                    value={contactForm.priority}
                    onChange={(e) =>
                      setContactForm((prev) => ({ ...prev, priority: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">{t('help.contactForm.priorityLow')}</option>
                    <option value="normal">{t('help.contactForm.priorityNormal')}</option>
                    <option value="high">{t('help.contactForm.priorityHigh')}</option>
                    <option value="urgent">{t('help.contactForm.priorityUrgent')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('help.contactForm.message')}
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm((prev) => ({ ...prev, message: e.target.value }))
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    placeholder={t('help.contactForm.messagePlaceholder')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('help.contactForm.sending')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('help.contactForm.send')}
                    </>
                  )}
                </button>
              </form>
            </div>

            {}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">{t('help.otherContactMethods')}</h3>

              <div className="space-y-4">
                <a
                  href={`mailto:${t('help.emailAddress')}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-800">{t('help.emailLabel')}</p>
                    <p className="text-sm text-slate-500">{t('help.emailAddress')}</p>
                  </div>
                </a>

                <a
                  href={`tel:${t('help.phoneNumber').replace(/\s/g, '')}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-800">{t('help.phoneLabel')}</p>
                    <p className="text-sm text-slate-500">{t('help.phoneNumber')}</p>
                    <p className="text-xs text-slate-400">{t('help.phoneHours')}</p>
                  </div>
                </a>
              </div>
            </div>

            {}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">{t('help.systemStatus')}</h3>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <div>
                  <p className="font-medium text-green-700">{t('help.allSystemsOperational')}</p>
                  <p className="text-sm text-slate-500">{t('help.lastUpdate')}: {t('help.ago')} 5 {t('help.min')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mt-10">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('help.security')}</h2>
                <p className="text-slate-400">{t('help.securitySubtitle')}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {}
              <button
                onClick={() => setActiveModal('cgu')}
                className="p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-left transition-all group"
              >
                <Scale className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="font-semibold mb-1">{t('help.cgu')}</h3>
                <p className="text-sm text-slate-400">{t('help.cguDescription')}</p>
              </button>

              {}
              <button
                onClick={() => setActiveModal('privacy')}
                className="p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-left transition-all group"
              >
                <Eye className="w-8 h-8 text-green-400 mb-3" />
                <h3 className="font-semibold mb-1">{t('help.privacy')}</h3>
                <p className="text-sm text-slate-400">{t('help.privacyDescription')}</p>
              </button>

              {}
              <button
                onClick={() => setActiveModal('cookies')}
                className="p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-left transition-all group"
              >
                <Cookie className="w-8 h-8 text-amber-400 mb-3" />
                <h3 className="font-semibold mb-1">{t('help.cookies')}</h3>
                <p className="text-sm text-slate-400">{t('help.cookiesDescription')}</p>
              </button>

              {}
              <button
                onClick={() => setActiveModal('security')}
                className="p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-left transition-all group"
              >
                <Lock className="w-8 h-8 text-purple-400 mb-3" />
                <h3 className="font-semibold mb-1">{t('help.securityPolicy')}</h3>
                <p className="text-sm text-slate-400">{t('help.securityPolicyDescription')}</p>
              </button>
            </div>

            {}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full">
                  <ShieldCheck className="w-4 h-4 text-green-400" />
                  <span className="text-sm">{t('help.gdprCompliant')}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full">
                  <Lock className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">{t('help.encryption')}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full">
                  <Server className="w-4 h-4 text-purple-400" />
                  <span className="text-sm">{t('help.euHosting')}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full">
                  <Fingerprint className="w-4 h-4 text-amber-400" />
                  <span className="text-sm">{t('help.twoFactorAvailable')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      {activeModal === 'cgu' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <Scale className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold text-slate-800">{t('help.modals.cgu.title')}</h2>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              <p className="text-sm text-slate-500 mb-4">{t('help.modals.cgu.lastUpdated')} : {t('help.modals.cgu.lastUpdatedDate')}</p>

              <div className="space-y-6 text-slate-600">
                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cgu.section1Title')}</h3>
                  <p>{t('help.modals.cgu.section1Content')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cgu.section2Title')}</h3>
                  <p>{t('help.modals.cgu.section2Content')}</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    {(modals.cgu?.section2List || []).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cgu.section3Title')}</h3>
                  <p>{t('help.modals.cgu.section3Content')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cgu.section4Title')}</h3>
                  <p>{t('help.modals.cgu.section4Content')}</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    {(modals.cgu?.section4List || []).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cgu.section5Title')}</h3>
                  <p>{t('help.modals.cgu.section5Content')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cgu.section6Title')}</h3>
                  <p>{t('help.modals.cgu.section6Content')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cgu.section7Title')}</h3>
                  <p>{t('help.modals.cgu.section7Content')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cgu.section8Title')}</h3>
                  <p>{t('help.modals.cgu.section8Content')}</p>
                </section>
              </div>
            </div>
            <div className="p-4 pb-6 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setActiveModal(null)}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('help.understood')}
              </button>
            </div>
          </div>
        </div>
      )}

      {}
      {activeModal === 'privacy' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <Eye className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-slate-800">{t('help.modals.privacy.title')}</h2>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              <p className="text-sm text-slate-500 mb-4">{t('help.modals.privacy.lastUpdated')} : {t('help.modals.privacy.lastUpdatedDate')}</p>

              <div className="space-y-6 text-slate-600">
                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.privacy.section1Title')}</h3>
                  <p>{t('help.modals.privacy.section1Content')}</p>
                  <p className="mt-2">{t('help.modals.privacy.section1Email')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.privacy.section2Title')}</h3>
                  <p>{t('help.modals.privacy.section2Content')}</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    {(modals.privacy?.section2List || []).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.privacy.section3Title')}</h3>
                  <p>{t('help.modals.privacy.section3Content')}</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    {(modals.privacy?.section3List || []).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.privacy.section4Title')}</h3>
                  <p>{t('help.modals.privacy.section4Content')}</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    {(modals.privacy?.section4List || []).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.privacy.section5Title')}</h3>
                  <p>{t('help.modals.privacy.section5Content')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.privacy.section6Title')}</h3>
                  <p>{t('help.modals.privacy.section6Content')}</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    {(modals.privacy?.section6List || []).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                  <p className="mt-2">{t('help.modals.privacy.section6Contact')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.privacy.section7Title')}</h3>
                  <p>{t('help.modals.privacy.section7Content')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.privacy.section8Title')}</h3>
                  <p>{t('help.modals.privacy.section8Content')}</p>
                </section>
              </div>
            </div>
            <div className="p-4 pb-6 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setActiveModal(null)}
                className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('help.understood')}
              </button>
            </div>
          </div>
        </div>
      )}

      {}
      {activeModal === 'cookies' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <Cookie className="w-6 h-6 text-amber-600" />
                <h2 className="text-xl font-bold text-slate-800">{t('help.modals.cookies.title')}</h2>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              <p className="text-sm text-slate-500 mb-4">{t('help.modals.cookies.lastUpdated')} : {t('help.modals.cookies.lastUpdatedDate')}</p>

              <div className="space-y-6 text-slate-600">
                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cookies.section1Title')}</h3>
                  <p>{t('help.modals.cookies.section1Content')}</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cookies.section2Title')}</h3>

                  <div className="mt-4 space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2">{t('help.modals.cookies.essential')}</h4>
                      <p className="text-sm text-green-700">{t('help.modals.cookies.essentialDesc')}</p>
                      <ul className="list-disc list-inside mt-2 text-sm text-green-700">
                        {(modals.cookies?.essentialList || []).map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2">{t('help.modals.cookies.analytics')}</h4>
                      <p className="text-sm text-blue-700">{t('help.modals.cookies.analyticsDesc')}</p>
                      <ul className="list-disc list-inside mt-2 text-sm text-blue-700">
                        {(modals.cookies?.analyticsList || []).map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-semibold text-purple-800 mb-2">{t('help.modals.cookies.personalization')}</h4>
                      <p className="text-sm text-purple-700">{t('help.modals.cookies.personalizationDesc')}</p>
                      <ul className="list-disc list-inside mt-2 text-sm text-purple-700">
                        {(modals.cookies?.personalizationList || []).map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cookies.section3Title')}</h3>
                  <table className="w-full mt-2 text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2 rounded-tl-lg">{t('help.modals.cookies.cookieType')}</th>
                        <th className="text-left p-2 rounded-tr-lg">{t('help.modals.cookies.duration')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="p-2">{t('help.modals.cookies.session')}</td>
                        <td className="p-2">{t('help.modals.cookies.sessionDuration')}</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="p-2">{t('help.modals.cookies.authentication')}</td>
                        <td className="p-2">{t('help.modals.cookies.authDuration')}</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="p-2">{t('help.modals.cookies.preferences')}</td>
                        <td className="p-2">{t('help.modals.cookies.prefDuration')}</td>
                      </tr>
                      <tr>
                        <td className="p-2">{t('help.modals.cookies.analytics')}</td>
                        <td className="p-2">{t('help.modals.cookies.analyticsDuration')}</td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('help.modals.cookies.section4Title')}</h3>
                  <p>{t('help.modals.cookies.section4Content')}</p>
                  <p className="mt-2">{t('help.modals.cookies.section4Content2')}</p>
                </section>
              </div>
            </div>
            <div className="p-4 pb-6 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setActiveModal(null)}
                className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('help.understood')}
              </button>
            </div>
          </div>
        </div>
      )}

      {}
      {activeModal === 'security' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-slate-800">{t('help.modals.security.title')}</h2>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              <div className="space-y-6 text-slate-600">
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                  <p className="text-purple-800 font-medium">{t('help.modals.security.priority')}</p>
                </div>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Key className="w-5 h-5 text-purple-600" />
                    {t('help.modals.security.encryption')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.encryptionTransit')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.encryptionRest')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.encryptionPasswords')}</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Fingerprint className="w-5 h-5 text-purple-600" />
                    {t('help.modals.security.authentication')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.twoFactor')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.sessions')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.detection')}</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Server className="w-5 h-5 text-purple-600" />
                    {t('help.modals.security.infrastructure')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.euHosting')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.backups')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.availability')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.ddos')}</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-purple-600" />
                    {t('help.modals.security.compliance')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.gdpr')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.audits')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{t('help.modals.security.logs')}</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">{t('help.modals.security.reportVulnerability')}</h3>
                  <p>{t('help.modals.security.reportVulnerabilityContent')}</p>
                  <a href="mailto:security@covenant-tracker.com" className="inline-flex items-center gap-2 mt-2 text-purple-600 hover:underline">
                    <Mail className="w-4 h-4" />
                    security@covenant-tracker.com
                  </a>
                </section>
              </div>
            </div>
            <div className="p-4 pb-6 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setActiveModal(null)}
                className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('help.understood')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Help;